import React, {useEffect} from "react";

import styles from "./FranchiseForm.module.css";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FormHeader} from "../../../elements/formHeader/FormHeader";
import {Section} from "../../../elements/section/Section";
import {ValidatedForm} from "../../../elements/validator/validatedForm/ValidatedForm";
import {ValidatedInputField} from "../../../elements/validator/component/ValidatedInputField";
import {ValidatedMultiSelectField} from "../../../elements/validator/component/ValidatedMultiSelectField";
import {ValidatedSelectField} from "../../../elements/validator/component/ValidatedSelectField";
import {
    CustomSwitchFieldWithTitle
} from "../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {
    DefaultValidValidator,
    MultipleEmailValidator,
    NotEmptyStringValidator,
    SameDataProviderLocationsValidator
} from "../../../elements/validator/Validator";
import {SelectOption} from "../../../elements/select/SelectDropdown";
import {getVehicleTypeTitle} from "../../../../utils/enumValueTitleUtil";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {LocationOptionDTO, NewVehicleDataConfigurationDTOVehicleTypeEnum} from "../../../../api/generated";
import {
    franchiseLocationOptionsSelector
} from "../franchiseSlice";
import {FranchiseRequestUi} from "../../assistant/franchiseTypes";
import {RootState} from "../../../../app/store";
import {
    fetchNewVehicleManufacturerOptions,
    newVehicleManufacturerOptionsRequestStatusSelector,
    newVehicleManufacturerOptionsSelector,
    resetNewVehicleManufacturerOptionsState
} from "../newVehicleManufacturerOptionsSlice";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {useRequestErrorToast} from "../../../../hooks/useRequestErrorToast";

type FranchiseFormRequest = Partial<FranchiseRequestUi & {
    newVehicleVehicleType: NewVehicleDataConfigurationDTOVehicleTypeEnum;
    newVehicleManufacturer: string;
}>;

const VEHICLE_TYPE_OPTIONS: SelectOption<NewVehicleDataConfigurationDTOVehicleTypeEnum>[] = [
    {value: NewVehicleDataConfigurationDTOVehicleTypeEnum.Car, label: getVehicleTypeTitle(NewVehicleDataConfigurationDTOVehicleTypeEnum.Car)},
    {value: NewVehicleDataConfigurationDTOVehicleTypeEnum.Lcv, label: getVehicleTypeTitle(NewVehicleDataConfigurationDTOVehicleTypeEnum.Lcv)},
    {value: NewVehicleDataConfigurationDTOVehicleTypeEnum.Bike, label: getVehicleTypeTitle(NewVehicleDataConfigurationDTOVehicleTypeEnum.Bike)}
];

interface Props {
    title: string;
    onClose: () => void;
    onSubmit: () => void;
    requestSelector: (state: RootState) => FranchiseFormRequest;
    updateRequest: (update: FranchiseFormRequest) => any;
}

const selectRequestValue = <PROPERTY, >(
    requestSelector: (state: RootState) => FranchiseFormRequest,
    mapper: (request: FranchiseFormRequest) => PROPERTY | undefined
) => (state: RootState) => mapper(requestSelector(state));

const selectRequestLocations = (
    requestSelector: (state: RootState) => FranchiseFormRequest
) => (state: RootState): LocationOptionDTO[] =>
    (requestSelector(state).publicLocationIdList || []).filter(location => !!location?.publicId);

export const FranchiseForm: React.FC<Props> = ({
                                                   title,
                                                   onClose,
                                                   onSubmit,
                                                   requestSelector,
                                                   updateRequest
                                               }) => {
    const dispatch = useAppDispatch();
    const locations = useAppSelector(franchiseLocationOptionsSelector);
    const newVehicleManufacturerOptions = useAppSelector(newVehicleManufacturerOptionsSelector);
    const manufacturerOptionsRequestStatus = useAppSelector(newVehicleManufacturerOptionsRequestStatusSelector);
    const newVehicleDataConfiguration = useAppSelector(selectRequestValue(
        requestSelector,
        request => request.newVehicleDataConfiguration
    ));
    const newVehiclesEnabled = !!newVehicleDataConfiguration?.isEnabled;
    const vehicleType = newVehicleDataConfiguration?.vehicleType;

    useEffect(() => {
        dispatch(resetNewVehicleManufacturerOptionsState());
        if (!vehicleType) {
            return;
        }
        dispatch(fetchNewVehicleManufacturerOptions(vehicleType));
    }, [dispatch, vehicleType]);

    useRequestErrorToast(manufacturerOptionsRequestStatus, "Failed to load vehicle manufacturers");

    const locationOptions: SelectOption<LocationOptionDTO>[] = locations
        .map(location => ({value: location, label: location.locationName || location.publicId || ""}));

    const updateNewVehiclesEnabled = (isEnabled: boolean) => {
        dispatch(updateRequest({
            newVehicleDataConfiguration: {
                id: newVehicleDataConfiguration?.id,
                isEnabled,
                vehicleType: vehicleType || NewVehicleDataConfigurationDTOVehicleTypeEnum.Car,
                manufacturer: newVehicleDataConfiguration?.manufacturer
            }
        }));
    };

    const updateNewVehicleType = (newVehicleType: NewVehicleDataConfigurationDTOVehicleTypeEnum) => {
        dispatch(updateRequest({
            newVehicleDataConfiguration: {
                id: newVehicleDataConfiguration?.id,
                isEnabled: true,
                vehicleType: newVehicleType,
                manufacturer: undefined
            }
        }));
    };

    const updateNewVehicleManufacturer = (manufacturer: string) => {
        if (!vehicleType) {
            return;
        }
        if (manufacturerOptionsRequestStatus === RequestStatus.REJECTED && newVehicleManufacturerOptions.length === 0) {
            dispatch(resetNewVehicleManufacturerOptionsState());
        }
        dispatch(updateRequest({
            newVehicleDataConfiguration: {
                id: newVehicleDataConfiguration?.id,
                isEnabled: true,
                vehicleType,
                manufacturer
            }
        }));
    };

    return <ValidatedForm<FranchiseFormRequest> submitForm={onSubmit}>
        {({formDisabled, onSubmitClick, updateValidationStatus, getMessage}) => {
            const getFieldMessage = (fieldName: string) => getMessage(fieldName as keyof FranchiseFormRequest) || "";

            return <div className={styles.wrapper}>
                <FormHeader formTitle={title} onClose={onClose}/>
                <div className={styles.fieldsWrapper}>
                    <Section title={"Franchise Information"}>
                        <div className={styles.fieldsLine}>
                            <ValidatedInputField<string, FranchiseFormRequest>
                                onChange={(name) => dispatch(updateRequest({name}))}
                                updateValidationStatus={updateValidationStatus}
                                fieldName={"name"}
                                selector={selectRequestValue(requestSelector, request => request.name)}
                                validator={NotEmptyStringValidator}
                                label={"Name"}
                                placeholder={"Franchise name"}
                                getMessage={getFieldMessage}
                            />
                            <ValidatedMultiSelectField<LocationOptionDTO, FranchiseFormRequest>
                                onChange={(publicLocationIdList) => dispatch(updateRequest({
                                    publicLocationIdList: publicLocationIdList.filter(location => !!location?.publicId)
                                }))}
                                updateValidationStatus={updateValidationStatus}
                                fieldName={"publicLocationIdList"}
                                selector={selectRequestLocations(requestSelector)}
                                validator={SameDataProviderLocationsValidator}
                                label={"Locations"}
                                getMessage={getFieldMessage}
                                options={locationOptions}
                                isEqual={(a, b) => !!a?.publicId && !!b?.publicId && a.publicId === b.publicId}
                            />
                        </div>
                        <div className={styles.fieldsLineWithMargin}>
                            <ValidatedInputField<string, FranchiseFormRequest>
                                required={false}
                                onChange={(urlPathPattern) => dispatch(updateRequest({urlPathPattern}))}
                                updateValidationStatus={updateValidationStatus}
                                fieldName={"urlPathPattern"}
                                selector={selectRequestValue(requestSelector, request => request.urlPathPattern)}
                                validator={DefaultValidValidator}
                                label={"URL Path Pattern"}
                                placeholder={"/bmw/"}
                                getMessage={getFieldMessage}
                            />
                            <ValidatedInputField<string, FranchiseFormRequest>
                                required={false}
                                onChange={(franchiseHash) => dispatch(updateRequest({franchiseHash}))}
                                updateValidationStatus={updateValidationStatus}
                                fieldName={"franchiseHash"}
                                selector={selectRequestValue(requestSelector, request => request.franchiseHash)}
                                validator={DefaultValidValidator}
                                label={"Franchise Hash"}
                                placeholder={"Keyloop franchise hash"}
                                getMessage={getFieldMessage}
                            />
                        </div>
                    </Section>
                    <Section title={"New Vehicles"}>
                        <CustomSwitchFieldWithTitle
                            textOnTrue={"On"}
                            textOnFalse={"Off"}
                            title={"New Vehicles"}
                            description={"Enable New Vehicles"}
                            valueSelector={selectRequestValue(requestSelector, request => request.newVehicleDataConfiguration?.isEnabled)}
                            toggleValue={updateNewVehiclesEnabled}
                        />
                        {newVehiclesEnabled &&
                            <div className={styles.fieldsLineWithMargin}>
                                <ValidatedSelectField<NewVehicleDataConfigurationDTOVehicleTypeEnum, FranchiseFormRequest>
                                    onChange={updateNewVehicleType}
                                    updateValidationStatus={updateValidationStatus}
                                    fieldName={"newVehicleVehicleType"}
                                    selector={selectRequestValue(requestSelector, request => request.newVehicleDataConfiguration?.vehicleType)}
                                    validator={DefaultValidValidator}
                                    label={"Vehicle Type"}
                                    getMessage={getFieldMessage}
                                    options={VEHICLE_TYPE_OPTIONS}
                                    selectFirstOptionByDefault={true}
                                />
                                <ValidatedSelectField<string, FranchiseFormRequest>
                                    onChange={updateNewVehicleManufacturer}
                                    updateValidationStatus={updateValidationStatus}
                                    fieldName={"newVehicleManufacturer"}
                                    selector={selectRequestValue(requestSelector, request => request.newVehicleDataConfiguration?.manufacturer)}
                                    validator={NotEmptyStringValidator}
                                    label={"New Vehicle Manufacturer"}
                                    getMessage={getFieldMessage}
                                    options={newVehicleManufacturerOptions}
                                    selectFirstOptionByDefault={false}
                                    disabled={!vehicleType}
                                />
                            </div>
                        }
                    </Section>
                    <Section title={"Communication"}>
                        <div className={styles.fieldsLine}>
                            <ValidatedInputField<string, FranchiseFormRequest>
                                required={false}
                                onChange={(franchiseEmailTranscriptRecipients) => dispatch(updateRequest({franchiseEmailTranscriptRecipients}))}
                                updateValidationStatus={updateValidationStatus}
                                fieldName={"franchiseEmailTranscriptRecipients"}
                                selector={selectRequestValue(requestSelector, request => request.franchiseEmailTranscriptRecipients)}
                                validator={MultipleEmailValidator}
                                label={"Transcript Recipient(s)"}
                                placeholder={"example@mail.com; second@mail.com"}
                                getMessage={getFieldMessage}
                            />
                        </div>
                        <CustomSwitchFieldWithTitle
                            textOnTrue={"On"}
                            textOnFalse={"Off"}
                            title={"Transcripts Filtration"}
                            description={"Only chats with a stored user email or phone number will be sent"}
                            valueSelector={selectRequestValue(requestSelector, request => request.franchiseEmailFiltrationEnabled)}
                            toggleValue={(franchiseEmailFiltrationEnabled) => dispatch(updateRequest({franchiseEmailFiltrationEnabled}))}
                        />
                        <CustomSwitchFieldWithTitle
                            textOnTrue={"On"}
                            textOnFalse={"Off"}
                            title={"Transcript Summary"}
                            description={"Generate summary for franchise transcripts when trigger tools are used"}
                            valueSelector={selectRequestValue(requestSelector, request => request.franchiseEmailTranscriptSummaryEnabled)}
                            toggleValue={(franchiseEmailTranscriptSummaryEnabled) => dispatch(updateRequest({franchiseEmailTranscriptSummaryEnabled}))}
                        />
                    </Section>
                    <Section title={"Focus behaviour"}>
                        <CustomSwitchFieldWithTitle
                            textOnTrue={"On"}
                            textOnFalse={"Off"}
                            title={"Can be in focus"}
                            description={"When off, this franchise and its locations never become Aime's active franchise, not even when the page hash or URL matches it."}
                            tooltip={"Franchise transcript recipients only receive transcripts while this franchise is in focus, so with this off they will rarely get any. Location recipients are not affected."}
                            valueSelector={selectRequestValue(requestSelector, request => request.focusEnabled)}
                            toggleValue={(focusEnabled) => dispatch(updateRequest({focusEnabled}))}
                        />
                        <CustomSwitchFieldWithTitle
                            textOnTrue={"On"}
                            textOnFalse={"Off"}
                            title={"Share with focused franchises"}
                            description={"When on, this franchise is also available to Aime whenever another franchise is in focus."}
                            tooltip={"When a conversation uses one of its locations, for example for a booking, the transcript goes to that location's recipients, not to this franchise's recipients."}
                            valueSelector={selectRequestValue(requestSelector, request => request.sharedWithFocusedFranchises)}
                            toggleValue={(sharedWithFocusedFranchises) => dispatch(updateRequest({sharedWithFocusedFranchises}))}
                        />
                    </Section>
                </div>
                <div className={styles.buttonsWrapper}>
                    <Button title={"Close"} state={ButtonState.SECONDARY} handleClick={onClose}/>
                    <Button title={title} state={formDisabled ? ButtonState.DISABLED : ButtonState.PRIMARY}
                            handleClick={onSubmitClick}/>
                </div>
            </div>
        }}
    </ValidatedForm>
}
